import React, { useState, useEffect, useRef } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Dimensions,
  SafeAreaView,
  Alert,
  Image,
  ImageBackground,
  Modal,
} from "react-native";
import { LinearGradient } from "expo-linear-gradient";
import Constants from "expo-constants";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { Ionicons } from "@expo/vector-icons";
import { useNavigation, CommonActions, useFocusEffect } from "@react-navigation/native";
import { useBottomTabBarHeight } from "@react-navigation/bottom-tabs";
import {
  logout,
  getUserProfile,
  debugAsyncStorage,
  getSessionInfo,
  generateAutoLoginUrl,
} from "../services/authApi";
import { fetchUserProfile, doLogout } from "../services/userDataService";
import { openZoolyfeIdSso } from "../services/zoolyfeSsoService";
import HexpodIcon from "../components/icons/hexpod";
import ZoolyfeIcon from "../components/icons/zoolyfe";
import { API_BASE_URL } from '../config/api';
import { useFloatingButton } from '../contexts/FloatingButtonContext';
import { useCustomAlertModal } from '../components/CustomAlertModal';
import { MaskedTextInput } from "react-native-mask-text";

const { width, height } = Dimensions.get("window");

export default function ProfileScreen({ navigation }) {
  const { setScrollY, setIsHomeFocused, setSpinFloatingContent } = useFloatingButton();
  const [userData, setUserData] = useState(null);
  const scrollViewRef = useRef(null);
  const menuSectionYRef = useRef(0);
  const tabBarHeight = useBottomTabBarHeight();
  const { 
    customAlertModal, 
    showCustomAlert, 
    hideCustomAlert, 
    handleCustomAlertConfirm
  } = useCustomAlertModal();

  const [qrShown, setQrShown] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [errorMsg, setErrorMsg] = useState(``);
  const [zoolyfeId, setZoolyfeId] = useState({
    visible: false,
    value: '',
    prefix: '',
    suffix: '',
    editable: 'forbidden',
  });

  const showZoolyfeIdModal = () => {
    setZoolyfeId(prev => ({ ...prev, visible: true }))
  }

  const hideZoolyfeIdModal = () => {
    setErrorMsg(``);
    setZoolyfeId(prev => ({ ...prev, visible: false }))
  }

  useFocusEffect(
    React.useCallback(() => {
      loadUserData();
    }, [navigation])
  );

  let alertTimer = null;

  const storeDataToLocalVar = async (data) => {
    const userData = {
      ...data,
      user_id: data.user_id,
      name: data.user_fullname || data.name || "User",
      email: data.user_email,
      phone: data.phone || "Not provided",
      points: data.points || 0,
      stamps: data.stamps || 0,
      memberSince: data.created_at
        ? data.created_at.split("T")[0]
        : "2024-01-01",
    };
    setUserData(userData);

    if (userData?.user_zoolyfe_id) {
      setZoolyfeId(prev => ({ 
        ...prev, 
        value: userData?.user_zoolyfe_id,
        prefix: userData?.user_zoolyfe_id_prefix,
        suffix: userData?.user_zoolyfe_id_suffix,
        editable: userData?.user_zoolyfe_id_updatable || 'forbidden',
      }));
    }
  }

  const loadUserData = async () => {
    try {
      const userToken = await AsyncStorage.getItem("userToken");
      const sessionId = await AsyncStorage.getItem("session_id");

      if (!userToken || !sessionId) {
        navigation.replace("Login");
        return;
      }

      // For authenticated users, fetch fresh data from API
      // Try to fetch fresh data from backend first
      try {
        const response = await fetchUserProfile();
        if (response.data) {
          storeDataToLocalVar(response.data);
          return; // Exit early if backend fetch was successful
        }
      } catch (backendError) {
        console.log(
          "Backend fetch failed, using cached data:",
          backendError.message
        );
      }

      // Fallback to cached data if backend fetch fails
      const userDataString = await AsyncStorage.getItem("userData");
      if (userDataString) {
        const apiUserData = JSON.parse(userDataString);
        // Map API response fields to expected structure
        const userData = {
          ...apiUserData,
          name: apiUserData.user_fullname || apiUserData.name || "User",
          points: apiUserData.points || 0,
          stamps: apiUserData.stamps || 0,
          memberSince:
            apiUserData.memberSince ||
            (apiUserData.created_at
              ? apiUserData.created_at.split("T")[0]
              : "2024-01-01"),
        };
        setUserData(userData);
      }
    } catch (error) {
      console.log("Error loading user data:", error);
    }
  };

  const handleLogout = () => {
    console.log("handleLogout called");
    showCustomAlert(
      "Logout", 
      "Are you sure you want to logout?", 
      "error",
      doLogout,
    );
  };

  const handleEditProfile = () => {
    navigation.navigate("EditProfile");
  };

  const handleNotificationSettings = () => {
    showCustomAlert(
      "Notifications",
      "Notification settings coming soon!",
      "info"
    );
  };

  const handlePrivacyPolicy = () => {
    showCustomAlert(
      "Privacy Policy",
      "Privacy policy will be displayed here.",
      "info"
    );
  };

  const handleTermsOfService = () => {
    showCustomAlert(
      "Terms of Service",
      "Terms of service will be displayed here.",
      "info"
    );
  };

  const handleContactSupport = () => {
    showCustomAlert(
      "Contact Support",
      "Email: support@zoolyfe.com\nPhone: +1 (555) 123-4567\nHours: Mon-Fri 9AM-5PM",
      "info"
    );
  };

  const handleAbout = () => {
    showCustomAlert(
      "About Zoolyfe Club",
      "Version 1.0.0\n\nZoolyfe Club is your gateway to exclusive zoo experiences, rewards, and memorable moments with wildlife.\n\nDeveloped with ❤️ for animal lovers.",
      "info"
    );
  };

  const handleAutoLoginZoolyfe = async () => {
    try {
      showCustomAlert(
        "Auto Login",
        "Generating SSO login URL for zoolyfe.id...",
        "info",
        null,
        { timeout: 3000 }
      );
      await openZoolyfeIdSso();
    } catch (error) {
      console.error("SSO Auto-login error:", error);
      showCustomAlert(
        "SSO Login Failed",
        error.message || "Failed to generate SSO login URL. Please make sure you are logged in.",
        "error"
      );
    }
  };

  const handleAutoLoginHexpod = async () => {
    try {
      showCustomAlert(
        "Auto Login",
        "Generating SSO login URL for hexpod.id...",
        "info",
        null,
        { timeout: 3000 }
      );

      // Get auth_token from localStorage (stored as remember_token)
      const authToken = await AsyncStorage.getItem("auth_token");

      if (!authToken) {
        throw new Error("Authentication token not found. Please login again.");
      }

      // Call the documented /api/v1/createsso endpoint
      const response = await fetch(`${API_BASE_URL}/api/v1/createsso-hexpod`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          Accept: "application/json",
          "X-Api-Key":
            Constants.expoConfig?.extra?.API_KEY ||
            "9f3c7a2d1b84e6f5c0a9d27e4b6f8a1c5d3e9b7a0c2f4e6d8a1b3c5f7e9d2",
        },
        body: new URLSearchParams({
          auth_token: authToken,
        }),
      });

      const result = await response.json();

      if (result.status !== "success") {
        throw new Error(result.message || "Failed to generate SSO login URL");
      }

      const loginUrl = result.data.login_url;

      // Open the URL directly in a new browser tab
      window.open(loginUrl, "_blank");
    } catch (error) {
      console.error("SSO Auto-login error:", error);
      showCustomAlert(
        "SSO Login Failed",
        error.message || "Failed to generate SSO login URL. Please make sure you are logged in.",
        "error"
      );
    }
  };

  const handleUpdateZoolyfeId = async () => {
    setIsUpdating(true);

    try {
      const authToken = await AsyncStorage.getItem("auth_token");

      if (!authToken) {
        throw new Error("Authentication token not found. Please login again.");
      }

      const response = await fetch(`${API_BASE_URL}/api/v1/update-zoolyfe-id`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "application/json",
          "X-Api-Key":
            Constants.expoConfig?.extra?.API_KEY ||
            "9f3c7a2d1b84e6f5c0a9d27e4b6f8a1c5d3e9b7a0c2f4e6d8a1b3c5f7e9d2",
        },
        body: new URLSearchParams({
          auth_token: authToken,
          zoolyfe_id: zoolyfeId.value.toString().toUpperCase(),
        }),
      });

      const result = await response.json();

      if (result.status === 'success') {
        hideZoolyfeIdModal();

        showCustomAlert(
          'Notifications', 
          result.message || 'Berhasil menyimpan member ID',
          'success'
        );

        if (result.data && result.data.user_data) {
          storeDataToLocalVar(result.data.user_data);

          if (result.data.user_data.remember_token) {
            await AsyncStorage.setItem("auth_token", result.data.user_data.remember_token);
            await AsyncStorage.setItem("userToken", result.data.user_data.remember_token);
          }
          if (result.data.user_data.session_id) {
            await AsyncStorage.setItem("session_id", result.data.user_data.session_id);
          }
        }
      } else {
        throw new Error(result.message || 'Gagal menyimpan member ID');
      }
    } catch (error) {
      console.log(error.message);
      setErrorMsg(error.message || 'Terjadi kesalahan saat menyimpan member ID');

      setTimeout(() => setErrorMsg(``), 5000);
    } finally {
      setIsUpdating(false);
    }
  }

  const hideQr = () => {
    setQrShown(false);
  }

  const showQr = () => {
    setQrShown(true);
  }

  useEffect(() => {
    // Keep profile menu visible on smaller devices.
    if (!userData || height > 750) {
      return;
    }

    const timer = setTimeout(() => {
      if (scrollViewRef.current) {
        scrollViewRef.current.scrollTo({
          y: Math.max(menuSectionYRef.current - 16, 0),
          animated: true,
        });
      }
    }, 250);

    return () => clearTimeout(timer);
  }, [userData]);

  const renderProfileHeader = () => (
    <ImageBackground
      source={require("../../assets/top-background.png")}
      style={styles.profileHeader}
      resizeMode="cover"
    ></ImageBackground>
  );

  const renderProfileCard = () => (
    <View style={styles.profileCardContainer}>
      <View style={styles.avatarContainer}>
        {userData?.user_avatar ? (
          <Image 
            source={`${API_BASE_URL}/${userData.user_avatar}`}
            style={styles.avatar} 
            resizeMode='cover'
          />
        ) : (
          <View style={styles.avatarPlaceholder}>
            <Ionicons name="person" size={22} color="#F8FAFC" />
          </View>
        )}
      </View>

      <View style={styles.profileCard}>
        <View style={styles.nameSection}>
          <Text style={styles.userName}>{userData?.name || ""}</Text>
          <Text style={styles.userEmail}>{userData?.email || ""}</Text>
          {userData?.user_zoolyfe_id && (
            <Text style={styles.userMemberID}>{userData?.user_zoolyfe_id}</Text>
          )}
        </View>

        <View style={{ width: '100%', flexDirection: 'row', gap: 4 }}>
          <TouchableOpacity style={[styles.editButton, { flex: 1 }]} onPress={handleEditProfile}>
            <Ionicons name="create-outline" size={20} color="#1E293B" />
            <Text style={styles.editButtonText}>Edit Profil</Text>
          </TouchableOpacity>

          {userData?.user_zoolyfe_qr && (
            <TouchableOpacity style={[styles.editButton, { flex: 1 }]} onPress={showQr}>
              <Ionicons name="qr-code-outline" size={20} color="#1E293B" />
              <Text style={styles.editButtonText}>Member ID</Text>
            </TouchableOpacity>
          )}
        </View>
      </View>
    </View>
  );

  const renderStatsCards = () => (
    <View style={styles.statsContainer}>
      <View style={styles.statCard}>
        <View style={styles.statIcon}>
          <Ionicons name="star" size={24} color="#FF6B35" />
        </View>
        <Text style={styles.statValue}>{userData?.points || 0}</Text>
        <Text style={styles.statLabel}>Total Points</Text>
      </View>

      <View style={styles.statCard}>
        <View style={styles.statIcon}>
          <Ionicons name="bookmark" size={24} color="#4CAF50" />
        </View>
        <Text style={styles.statValue}>{userData?.stamps || 0}</Text>
        <Text style={styles.statLabel}>E-Stamps</Text>
      </View>

      <View style={styles.statCard}>
        <View style={styles.statIcon}>
          <Ionicons name="calendar" size={24} color="#2196F3" />
        </View>
        <Text style={styles.statValue}>
          {userData?.memberSince
            ? new Date(userData.memberSince).getFullYear()
            : new Date().getFullYear()}
        </Text>
        <Text style={styles.statLabel}>Member Since</Text>
      </View>
    </View>
  );

  const renderMenuItem = (
    icon,
    title,
    subtitle,
    onPress,
    showArrow = true,
    color = "#333"
  ) => (
    <TouchableOpacity style={styles.menuItem} onPress={onPress}>
      <View style={styles.menuIcon}>
        {typeof icon === "string" ? (
          <Ionicons name={icon} size={24} color={color} />
        ) : (
          icon
        )}
      </View>
      <View style={styles.menuContent}>
        <Text style={styles.menuTitle}>{title}</Text>
        {subtitle && <Text style={styles.menuSubtitle}>{subtitle}</Text>}
      </View>
      {showArrow && <Ionicons name="chevron-forward" size={20} color="#CCC" />}
    </TouchableOpacity>
  );

  if (!userData) {
    return (
      <View style={styles.loadingContainer}>
        <Text style={styles.loadingContainerText}>Loading...</Text>
      </View>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <ScrollView
        ref={scrollViewRef}
        style={styles.scrollView}
        showsVerticalScrollIndicator={false}
        contentContainerStyle={[
          styles.scrollContent,
          { paddingBottom: tabBarHeight + 28 },
        ]}
        onScroll={(e) => setScrollY(e.nativeEvent.contentOffset.y)}
        scrollEventThrottle={16}
      >
        {renderProfileHeader()}
        {renderProfileCard()}

        <View
          style={styles.menuSection}
          onLayout={(event) => {
            menuSectionYRef.current = event.nativeEvent.layout.y;
          }}
        >
          <View style={styles.menuContainer}>
            {userData?.user_zoolyfe_id && zoolyfeId.editable != 'forbidden' && renderMenuItem(
              "create",
              "Ubah Member ID",
              null,
              () => showZoolyfeIdModal(),
              true,
              "#64748B"
            )}
            {userData?.user_zoolyfe_id && zoolyfeId.editable != 'forbidden' && (<View style={styles.menuDivider} />)}

            {renderMenuItem(
              "scan",
              "Scan QR Reward",
              null,
              () => navigation.navigate("Scan"),
              true,
              "#64748B"
            )}
            <View style={styles.menuDivider} />
            {renderMenuItem(
              "receipt-outline",
              "Riwayat Point Reward",
              null,
              () => navigation.navigate("Points"),
              true,
              "#64748B"
            )}
            <View style={styles.menuDivider} />
            {renderMenuItem(
              (
                <Image 
                  source={require('../../assets/icons/z-point-solid.svg')} 
                  style={{ 
                    width: 24, 
                    height: 24, 
                    tintColor: "#64748B"
                  }}
                  resizeMode="contain"
                />
              ),
              "Riwayat Tukar Poin",
              "Penukaran voucher dan hadiah fisik",
              () => navigation.navigate("TukarPoinHistory"),
              true,
              "#64748B"
            )}
            <View style={styles.menuDivider} />
            {renderMenuItem(
              "star",
              "Riwayat E-Stamp",
              null,
              () => navigation.navigate("History"),
              true,
              "#64748B"
            )}
            <View style={styles.menuDivider} />
            {renderMenuItem(
              "document-text",
              "Riwayat Spinning Wheel",
              "Data riwayat klaim hadiah spinning wheels",
              () => navigation.navigate("SpinPrizeClaimHistory"),
              true,
              "#64748B"
            )}
            <View style={styles.menuDivider} />
            {renderMenuItem(
              "link-outline",
              "Kode Referral Saya",
              null,
              () => navigation.navigate("Referral"),
              true,
              "#64748B"
            )}
            <View style={styles.menuDivider} />
            {renderMenuItem(
              "location-outline",
              "Alamat pengiriman",
              "Untuk hadiah & pesanan zoolyfe.id",
              () => navigation.navigate("ShippingAddressesList"),
              true,
              "#64748B"
            )}
            <View style={styles.menuDivider} />
            {renderMenuItem(
              "card-outline",
              "Verfikasi Identitas",
              "Diperlukan untuk bertransaksi di zoolyfe.id",
              () => navigation.navigate("IdentityUpload"),
              true,
              "#64748B"
            )}
            <View style={styles.menuDivider} />
            {renderMenuItem(
              <ZoolyfeIcon width={24} height={24} color="#64748B" />,
              "Login ke Zoolyfe.id",
              null,
              handleAutoLoginZoolyfe,
              true,
              "#64748B"
            )}
            <View style={styles.menuDivider} />
            {renderMenuItem(
              <HexpodIcon width={24} height={24} color="#64748B" />,
              "Login ke Hexpod",
              null,
              handleAutoLoginHexpod,
              true,
              "#64748B"
            )}
            <View style={styles.menuDivider} />
            {renderMenuItem(
              "log-out-outline",
              "Keluar Akun",
              null,
              handleLogout,
              true,
              "#EF4444"
            )}
          </View>
        </View>

        <View style={styles.bottomSpacing} />
      </ScrollView>

      {customAlertModal}
      
      <Modal
        animationType="fade"
        transparent={true}
        visible={qrShown}
        onRequestClose={() => {
          hideQr();
        }}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalBody}>
              <View style={styles.modalClose}>
                <TouchableOpacity onPress={hideQr}>
                  <Ionicons name="close" size={20} color="#1E293B" />
                </TouchableOpacity>
              </View>
              <View style={styles.qrWrapper}>
                <View style={styles.avatarWrapper}>
                  {userData?.user_avatar ? (
                    <Image 
                      source={`${API_BASE_URL}/${userData.user_avatar}`}
                      style={styles.avatar} 
                      resizeMode='cover'
                    />
                  ) : (
                    <View style={styles.avatarPlaceholder}>
                      <Ionicons name="person" size={22} color="#F8FAFC" />
                    </View>
                  )}
                </View>

                <Image 
                  source={userData?.user_zoolyfe_qr}
                  style={styles.qr} 
                  resizeMode='contain'
                />

                <View style={styles.idWrapper}>
                  <Text style={styles.idText}>{userData?.user_zoolyfe_id || ''}</Text>
                </View>
              </View>
            </View>
          </View>
        </View>
      </Modal>

      <Modal
        animationType="fade"
        transparent={true}
        visible={zoolyfeId.visible}
        onRequestClose={hideZoolyfeIdModal}
      >
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalBody}>
              <View style={styles.modalClose}>
                <TouchableOpacity onPress={hideZoolyfeIdModal}>
                  <Ionicons name="close" size={20} color="#1E293B" />
                </TouchableOpacity>
              </View>
              
              <View style={styles.formGroup}>
                <Text style={styles.label}>Member ID</Text>
                {zoolyfeId.editable == 'allowed' && (
                  <View style={styles.inputContainer}>
                    <MaskedTextInput
                      mask="SSSSSS 999999"
                      style={styles.input}
                      placeholder="XXXXXXXXXXXX"
                      placeholderTextColor="#94A3B8"
                      value={zoolyfeId.value}
                      onChangeText={(text, rawText) => {
                        setZoolyfeId(prev => ({ 
                          ...prev, 
                          value: rawText.toString().toUpperCase()
                        }));
                      }}
                      keyboardType="phone-pad"
                      autoCorrect={false}
                    />
                  </View>
                )}
                {zoolyfeId.editable == 'partial' && (
                  <View style={styles.inputContainer}>
                    <Text style={styles.inputPrefix}>{zoolyfeId.prefix}</Text>
                    <MaskedTextInput
                      mask="999999"
                      style={styles.input}
                      placeholder="XXXXXXXXXXXX"
                      placeholderTextColor="#94A3B8"
                      value={zoolyfeId.suffix}
                      onChangeText={(text, rawText) => {
                        setZoolyfeId(prev => ({ 
                          ...prev, 
                          suffix: rawText,
                          value: `${prev.prefix}${rawText}`.toString().toUpperCase() 
                        }));
                      }}
                      keyboardType="phone-pad"
                      autoCorrect={false}
                    />
                  </View>
                )}
                {errorMsg && (
                  <Text style={{ color: '#FF0000', fontFamily: 'Poppins', fontSize: 12, marginTop: 4 }}>{errorMsg}</Text>
                )}
              </View>

              <TouchableOpacity
                style={[styles.button, isUpdating && styles.disabledButton]}
                onPress={handleUpdateZoolyfeId}
                disabled={isUpdating}
              >
                <Text style={styles.buttonText}>
                  {isUpdating ? 'Menyimpan...' : 'Simpan'}
                </Text>
              </TouchableOpacity>

            </View>
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#F8F9FA",
  },
  loadingContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
  loadingContainerText: {
    fontFamily: 'Poppins',
  },
  scrollView: {
    flex: 1,
  },
  scrollContent: {
    paddingBottom: 24,
  },
  profileHeader: {
    position: "relative",
    paddingHorizontal: 16,
    paddingTop: 44,
    paddingBottom: 16,
    height: 200,
    overflow: "hidden",
  },
  decorativeElements: {
    position: "absolute",
    top: -35,
    right: 0,
    width: 201,
    height: 203,
    opacity: 0.08,
  },
  decorativeShape: {
    position: "absolute",
    backgroundColor: "#F8FAFC",
  },
  decorativeShape1: {
    width: 181,
    height: 163,
    borderRadius: 90,
    top: 0,
    left: 0,
  },
  decorativeShape2: {
    width: 181,
    height: 159,
    borderRadius: 90,
    top: 44,
    left: 20,
  },
  statusBar: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    paddingTop: 10,
    paddingHorizontal: 4,
  },
  statusTime: {
    fontSize: 16,
    fontWeight: "600",
    color: "#FFFFFF",
    fontFamily: "Poppins",
  },
  statusIcons: {
    flexDirection: "row",
    alignItems: "center",
  },
  battery: {
    width: 24,
    height: 12,
    borderWidth: 1,
    borderColor: "#FFFFFF",
    borderRadius: 2,
    marginLeft: 5,
    justifyContent: "center",
    alignItems: "flex-end",
  },
  batteryLevel: {
    width: 18,
    height: 8,
    backgroundColor: "#FFFFFF",
    borderRadius: 1,
  },
  profileCardContainer: {
    alignItems: "center",
    paddingHorizontal: 12,
    marginTop: -100,
    marginBottom: 24,
  },
  avatarContainer: {
    marginBottom: -32,
    zIndex: 1,
  },
  avatarPlaceholder: {
    width: 64,
    height: 64,
    borderRadius: 32,
    backgroundColor: "#64748B",
    justifyContent: "center",
    alignItems: "center",
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
    elevation: 4,
  },
  avatar: {
    width: 64,
    height: 64,
    borderRadius: 'calc(infinity * 1px)',
    boxShadow: "0px 4px 6px rgba(0, 0, 0, 0.1)",
    elevation: 4,
  },
  profileCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 8,
    paddingTop: 40,
    paddingHorizontal: 16,
    paddingBottom: 16,
    alignItems: "center",
    width: "100%",
    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
    elevation: 2,
    borderWidth: 1,
    borderColor: "#F1F5F9",
  },
  nameSection: {
    alignItems: "center",
    marginBottom: 16,
  },
  userName: {
    fontSize: 16,
    lineHeight: 24,
    fontWeight: "600",
    color: "#1E293B",
    marginBottom: 2,
    fontFamily: "Poppins",
    textAlign: "center",
  },
  userEmail: {
    fontSize: 12,
    lineHeight: 16,
    color: "#94A3B8",
    fontFamily: "Poppins",
    textAlign: "left",
    marginBottom: 4,
  },
  userMemberID: {
    fontSize: 14,
    color: "#1E293B",
    fontFamily: "Poppins-SemiBold",
    fontWeight: 600,
    textAlign: "left",
    letterSpacing: 2,
  },
  editButton: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "#FFFFFF",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: "#CBD5E1",
    gap: 8,
    width: "100%",
  },
  editButtonText: {
    fontSize: 14,
    fontWeight: "500",
    color: "#1E293B",
    fontFamily: "Poppins",
  },
  notificationButton: {
    width: 44,
    height: 44,
    borderRadius: 22,
    justifyContent: "center",
    alignItems: "center",
    padding: 10,
  },

  statsContainer: {
    flexDirection: "row",
    paddingHorizontal: 20,
    paddingVertical: 20,
    gap: 15,
  },
  statCard: {
    flex: 1,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 20,
    alignItems: "center",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.1)",
    elevation: 5,
  },
  statIcon: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: "#F8F9FA",
    justifyContent: "center",
    alignItems: "center",
    marginBottom: 8,
  },
  statValue: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#333",
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 12,
    color: "#666",
    textAlign: "center",
  },
  menuSection: {
    paddingHorizontal: 16,
    marginBottom: 20,
  },
  menuContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 6,
    overflow: "hidden",
    gap: 8,
    padding: 16,
  },
  menuItem: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    gap: 8,
  },
  menuIcon: {
    width: 20,
    height: 20,
    justifyContent: "center",
    alignItems: "center",
  },
  menuContent: {
    flex: 1,
  },
  menuTitle: {
    fontSize: 14,
    fontWeight: "500",
    color: "#1E293B",
    fontFamily: "Poppins",
  },
  menuSubtitle: {
    fontSize: 12,
    color: "#666",
    fontFamily: 'Poppins',
  },
  menuDivider: {
    height: 1,
    backgroundColor: "#F1F5F9",
    marginHorizontal: 0,
  },
  bottomSpacing: {
    height: 4,
  },

  // Modal styles
  modalOverlay: {
    flex: 1,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    justifyContent: "center",
    alignItems: "center",
    padding: 20,
  },
  modalContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    width: "100%",
    maxWidth: Math.min(width, 390),
    overflow: "hidden",
    elevation: 5,
    shadowColor: "#000",
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
  },
  modalBody: {
    margin: 16,
  },
  modalClose: {
    width: '100%',
    flexDirection: 'row-reverse',
    marginBottom: 16,
  },
  qrWrapper: {
    width: '100%',
    backgroundColor: '#F3F4F6',
    borderWidth: 1,
    borderColor: '#E5E7EB',
    borderRadius: 12,
    paddingBottom: 16,
    alignItems: 'center',
    gap: 16,
  },
  avatarWrapper: {
    marginTop: -32,
  },
  qr: {
    width: '75%',
    aspectRatio: 1,
    borderRadius: 8,
  },
  idWrapper: {
    width: '75%',
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    alignItems: 'center',
  },
  idText: {
    fontFamily: 'Poppins-SemiBold',
    fontWeight: 600,
    fontSize: 24,
    lineHeight: 32,
    color: '#1E293B',
    whiteSpace: 'nowrap',
    letterSpacing: width >= 410 ? 3 : 0,
  },

  formGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: 500,
    color: '#374151',
    marginBottom: 8,
    fontFamily: 'Poppins-Medium',
  },
  inputContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#FFFFFF',
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#AAAAAA',
    paddingHorizontal: 16,
    height: 48,
  },
  input: {
    flex: 1,
    width: '100%',
    fontSize: 16,
    color: '#1E293B',
    fontFamily: 'Poppins',
  },
  inputPrefix: {
    fontSize: 16,
    color: '#1E293B',
    fontFamily: 'Poppins',
    marginRight: 8,
  },
  button: {
    backgroundColor: '#000000',
    borderRadius: 6,
    padding: 12,
    justifyContent: 'center',
    alignItems: 'center',
  },
  disabledButton: {
    opacity: 0.7,
  },
  buttonText: {
    fontFamily: 'Poppins-Medium',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: 20,
    color: '#FFFFFF',
  },
});
